// src/pages/PartTypes/PartTypeForm.js
import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {partTypeService, productService, vendorService} from '../../services/api';
import { toast } from 'react-toastify';

// SearchableSelect component
const SearchableSelect = ({ id, options, value, onChange, placeholder }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = options.filter(option =>
                                               option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (option) => {
        onChange(option.id);
        setIsOpen(false);
        setSearchTerm('');
    };

    const selectedOption = options.find(option => option.id === value);

    return (
        <div className="relative">
            <div
                className="mt-1 p-3 flex border border-gray-300 rounded-md shadow-sm focus-within:ring-primary-500 focus-within:border-primary-500"
                onClick={() => setIsOpen(!isOpen)}
            >
                <input
                    type="text"
                    className="block w-full focus:outline-none sm:text-sm"
                    placeholder={placeholder}
                    value={searchTerm || (selectedOption ? selectedOption.label : '')}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>

            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option) => (
                            <div
                                key={option.id}
                                className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100"
                                onClick={() => handleSelect(option)}
                            >
                                {option.label}
                            </div>
                        ))
                    ) : (
                         <div className="relative cursor-default select-none py-2 pl-3 pr-9 text-gray-500">
                             No options found
                         </div>
                     )}
                </div>
            )}
        </div>
    );
};

const PartTypeForm = ({ isOpen, onClose, onSave, partType }) => {
    const [formData, setFormData] = useState({
                                                 name: '',
                                                 description: '',
                                                 is_sku_specific: false,
                                                 slug: '',
                                                 part_identifier: '',
                                                 current_stock: 0,
                                                 threshold: 0,
                                                 vendor_id: '',
                                                 vendor_sku: '',
                                                 vendor_price: '',
                                                 type: 'other',
                                                 dimension: '',  // New field for length/width
                                                 unit_type: 'in', // New field for unit type with default
                                                 quantity: 1,    // New field for quantity with default
                                             });

    const [products, setProducts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState('');
    const [skuSpecificInventory, setSkuSpecificInventory] = useState([]);

    // Fetch products and vendors when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchProducts();
            fetchVendors();

            if(partType?.id){
                fetchPartType()
            }
        }
    }, [isOpen]);

    // When part type changes or modal opens, update form data
    useEffect(() => {
        if (partType) {
            setFormData({
                            name: partType.name || '',
                            description: partType.description || '',
                            is_sku_specific: partType.is_sku_specific || false,
                            slug: partType.slug || '',
                            part_identifier: partType.part_identifier || '',
                            current_stock: partType?.current_stock || 0,
                            threshold: partType?.threshold || 0,
                            vendor_id: partType.vendor_id || '',
                            vendor_sku: partType.vendor_sku || '',
                            vendor_price: partType.vendor_price || '',
                            type: partType.type || 'other',
                            dimension: partType.dimension || '',
                            unit_type: partType.unit_type || 'in',
                            quantity: partType.quantity || 1,
                        });

            // If we have sku-specific inventory data, load it
            if (partType.skuInventory && partType.skuInventory.length > 0) {
                setSkuSpecificInventory(partType.skuInventory.map(item => ({
                    product_id: item.product_id,
                    current_stock: item.current_stock || 0,
                    threshold: item.threshold || 0,
                    product_sku: item.product?.sku || 'Unknown SKU'
                })));

                // Mark these products as selected
                setSelectedProducts(partType.skuInventory.map(item => item.product_id));
            }
        } else {
            // Reset form when adding a new part type
            setFormData({
                            name: '',
                            description: '',
                            is_sku_specific: false,
                            slug: '',
                            part_identifier: '',
                            current_stock: 0,
                            threshold: 0,
                            vendor_id: '',
                            vendor_sku: '',
                            vendor_price: '',
                            type: 'other',
                            dimension: '',
                            unit_type: 'in',
                            quantity: 1,
                        });
            setSkuSpecificInventory([]);
            setSelectedProducts([]);
        }
    }, [partType, isOpen]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await productService.getProducts();
            setProducts(response.data || []);
        } catch (err) {
            console.error('Error fetching products:', err);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const fetchPartType = async () => {
        try {
            setLoading(true);
            const response = await partTypeService.getPartType(partType.id);
            setSkuSpecificInventory(response.data.sku_inventory || []);
        } catch (err) {
            console.error('Error fetching sku inventory:', err);
            toast.error('Failed to load sku inventory data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchVendors = async () => {
        try {
            setLoading(true);
            const response = await vendorService.getVendors();
            setVendors(response || []);
        } catch (err) {
            console.error('Error fetching vendors:', err);
            toast.error('Failed to load vendors');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
                        ...formData,
                        [name]: type === 'checkbox' ? checked : value,
                    });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Prepare submission data based on whether this is SKU-specific
        const submissionData = {
            ...formData,
        };

        // Include SKU-specific inventory if applicable
        if (formData.is_sku_specific && skuSpecificInventory.length > 0) {
            submissionData.sku_inventory = skuSpecificInventory;
        }

        onSave(submissionData);
    };

    // Generate slug from name when name changes
    const handleNameChange = (e) => {
        const name = e.target.value;
        setFormData({
                        ...formData,
                        name,
                        slug: name.toLowerCase().replace(/\s+/g, '_'),
                    });
    };

    // Handle product selection for SKU-specific inventory
    const handleProductSelect = () => {
        if (!selectedProductId || selectedProducts.includes(selectedProductId)) {
            // Don't add if no product selected or already added
            return;
        }

        const selectedProduct = products.find(p => p.id === parseInt(selectedProductId));
        if (!selectedProduct) return;

        // Add to selected products
        setSelectedProducts([...selectedProducts, selectedProductId]);

        // Add to SKU-specific inventory with default values
        setSkuSpecificInventory([...skuSpecificInventory, {
            product_id: selectedProductId,
            current_stock: 0,
            threshold: 0,
            product_sku: selectedProduct.sku,
            product: selectedProduct,
        }]);

        // Reset selection
        setSelectedProductId('');
    };

    // Update SKU-specific inventory values
    const handleSkuInventoryChange = (index, field, value) => {
        const updatedInventory = [...skuSpecificInventory];
        updatedInventory[index][field] = value;
        setSkuSpecificInventory(updatedInventory);
    };

    // Remove a product from SKU-specific inventory
    const handleRemoveProduct = (index) => {
        const updatedInventory = [...skuSpecificInventory];
        const removedProductId = updatedInventory[index].product_id;

        // Remove from inventory
        updatedInventory.splice(index, 1);
        setSkuSpecificInventory(updatedInventory);

        // Remove from selected products
        setSelectedProducts(selectedProducts.filter(id => id !== removedProductId));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center px-6 py-4 border-b">
                    <h3 className="text-lg font-medium text-gray-900">
                        {partType ? 'Edit Part Type' : 'Add Part Type'}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="px-6 py-4 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Name */}
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleNameChange}
                                    required
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                            </div>

                            {/* Part Type Identifier */}
                            <div>
                                <label htmlFor="part_identifier" className="block text-sm font-medium text-gray-700">
                                    Part Type Identifier
                                </label>
                                <input
                                    type="text"
                                    name="part_identifier"
                                    id="part_identifier"
                                    value={formData.part_identifier}
                                    onChange={handleChange}
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Optional code or identifier for this part type
                                </p>
                            </div>
                            {/* Slug */}
                            <div>
                                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                                    Slug <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="slug"
                                    id="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Used for system identification. Lowercase, no spaces.
                                </p>
                            </div>
                            {/* Part Type */}
                            <div>
                                <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                                    Part Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    id="type"
                                    name="type"
                                    value={formData.type || 'other'}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                >
                                    <option value="foam">Foam</option>
                                    <option value="front_cover">Front Cover</option>
                                    <option value="back_cover">Back Cover</option>
                                    <option value="box">Box</option>
                                    <option value="magnet">Magnet</option>
                                    <option value="washer">Washer</option>
                                    <option value="other">Other</option>
                                </select>
                                <p className="mt-1 text-xs text-gray-500">
                                    Category of this part type.
                                </p>
                            </div>
                            {/* Current Stock */}
                            <div>
                                <label htmlFor="current_stock" className="block text-sm font-medium text-gray-700">
                                    Current Stock
                                </label>
                                <input
                                    type="text"
                                    name="current_stock"
                                    id="current_stock"
                                    value={formData.current_stock}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Current stock level of part type
                                </p>
                            </div>
                            {/* Threshold */}
                            <div>
                                <label htmlFor="threshold" className="block text-sm font-medium text-gray-700">
                                    Threshold
                                </label>
                                <input
                                    type="text"
                                    name="threshold"
                                    id="threshold"
                                    value={formData.threshold}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Threshold level of part type
                                </p>
                            </div>
                        </div>

                        {/* Material Specification - New Fields */}
                        <div className="bg-blue-50 p-4 rounded-md mb-4">
                            <h4 className="text-md font-medium text-gray-900 mb-2">Material Specifications</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {/* Dimension */}
                                <div>
                                    <label htmlFor="dimension" className="block text-sm font-medium text-gray-700">
                                        Dimension
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        name="dimension"
                                        id="dimension"
                                        value={formData.dimension}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                        placeholder="Enter length/width"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Length/width for material calculation
                                    </p>
                                </div>

                                {/* Unit Type */}
                                <div>
                                    <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700">
                                        Unit Type
                                    </label>
                                    <select
                                        id="unit_type"
                                        name="unit_type"
                                        value={formData.unit_type}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    >
                                        <option value="each">Each</option>
                                        <option value="in">Inches</option>
                                        <option value="ft">Feet</option>
                                        <option value="y">Yards</option>
                                        <option value="cm">Centimeters</option>
                                        <option value="m">Meters</option>
                                        <option value="mm">Millimeters</option>
                                        <option value="oz">Ounces</option>
                                        <option value="lb">Pounds</option>
                                        <option value="g">Grams</option>
                                        <option value="kg">Kilograms</option>
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Measurement unit
                                    </p>
                                </div>

                                {/* Quantity */}
                                <div>
                                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                        Quantity
                                    </label>
                                    <input
                                        type="number"
                                        name="quantity"
                                        id="quantity"
                                        min="1"
                                        value={formData.quantity}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Default quantity for cost calculation
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                name="description"
                                id="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>

                        {/* Vendor Information */}
                        <div className="bg-yellow-50 p-4 rounded-md mb-4">
                            <h4 className="text-md font-medium text-gray-900 mb-2">Vendor Information</h4>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Vendor Selection */}
                                <div className="sm:col-span-2">
                                    <label htmlFor="vendor_id" className="block text-sm font-medium text-gray-700">
                                        Vendor
                                    </label>
                                    <select
                                        id="vendor_id"
                                        name="vendor_id"
                                        value={formData.vendor_id}
                                        onChange={handleChange}
                                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    >
                                        <option value="">Select a vendor</option>
                                        {vendors.map(vendor => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Vendor SKU */}
                                <div>
                                    <label htmlFor="vendor_sku" className="block text-sm font-medium text-gray-700">
                                        Vendor SKU
                                    </label>
                                    <input
                                        type="text"
                                        name="vendor_sku"
                                        id="vendor_sku"
                                        value={formData.vendor_sku}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>

                                {/* Vendor Price */}
                                <div>
                                    <label htmlFor="vendor_price" className="block text-sm font-medium text-gray-700">
                                        Vendor Price
                                    </label>
                                    <div className="mt-1 relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 sm:text-sm">$</span>
                                        </div>
                                        <input
                                            type="number"
                                            name="vendor_price"
                                            id="vendor_price"
                                            min="0"
                                            step="0.01"
                                            value={formData.vendor_price}
                                            onChange={handleChange}
                                            className="p-3 pl-7 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Is SKU Specific */}
                        <div className="flex items-start">
                            <div className="flex items-center h-5">
                                <input
                                    id="is_sku_specific"
                                    name="is_sku_specific"
                                    type="checkbox"
                                    checked={formData.is_sku_specific}
                                    onChange={handleChange}
                                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                                />
                            </div>
                            <div className="ml-3 text-sm">
                                <label htmlFor="is_sku_specific" className="font-medium text-gray-700">
                                    SKU-Specific Part
                                </label>
                                <p className="text-gray-500">
                                    Check if this part type is specific to certain SKUs rather than generally available
                                </p>
                            </div>
                        </div>

                        {/* General Inventory (show if not SKU-specific) */}
                        {!formData.is_sku_specific && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {/* Current Stock */}
                                <div>
                                    <label htmlFor="current_stock" className="block text-sm font-medium text-gray-700">
                                        Current Stock
                                    </label>
                                    <input
                                        type="number"
                                        name="current_stock"
                                        id="current_stock"
                                        min="0"
                                        value={formData.current_stock}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    />
                                </div>

                                {/* Threshold */}
                                <div>
                                    <label htmlFor="threshold" className="block text-sm font-medium text-gray-700">
                                        Threshold
                                    </label>
                                    <input
                                        type="number"
                                        name="threshold"
                                        id="threshold"
                                        min="0"
                                        value={formData.threshold}
                                        onChange={handleChange}
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Minimum stock level before reordering
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* SKU-Specific Inventory (show if SKU-specific) */}
                        {formData.is_sku_specific && (
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-md">
                                    <h4 className="text-md font-medium text-gray-900 mb-2">SKU-Specific Inventory</h4>
                                    <p className="text-sm text-gray-500 mb-4">
                                        Add the products that use this part type and set inventory levels for each.
                                    </p>

                                    {/* Product selector */}
                                    <div className="flex items-end space-x-2 mb-4">
                                        <div className="flex-grow">
                                            <label htmlFor="product_select" className="block text-sm font-medium text-gray-700 mb-1">
                                                Add Product SKU
                                            </label>
                                            <SearchableSelect
                                                id="product_select"
                                                options={products.map(product => ({
                                                    id: product.id,
                                                    label: `${product.sku} ${product.description ? '- ' + product.description : ''}`
                                                }))}
                                                value={selectedProductId}
                                                onChange={setSelectedProductId}
                                                placeholder="Select a product SKU"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleProductSelect}
                                            className="bg-primary-600 p-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 h-12"
                                        >
                                            <PlusIcon className="h-5 w-5" />
                                        </button>
                                    </div>

                                    {/* SKU-specific inventory list */}
                                    {skuSpecificInventory.length === 0 ? (
                                        <div className="text-center py-4 text-gray-500 italic">
                                            No products added yet. Add products to track inventory for specific SKUs.
                                        </div>
                                    ) : (
                                         <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                                             <table className="min-w-full divide-y divide-gray-200">
                                                 <thead className="bg-gray-50">
                                                 <tr>
                                                     <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                         Product SKU
                                                     </th>
                                                     <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                         Current Stock
                                                     </th>
                                                     <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                         Threshold
                                                     </th>
                                                     <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                         Actions
                                                     </th>
                                                 </tr>
                                                 </thead>
                                                 <tbody className="bg-white divide-y divide-gray-200">
                                                 {skuSpecificInventory.map((item, index) => (
                                                     <tr key={index}>
                                                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                                             {item?.product?.sku ?? ''}-{partType.part_identifier}
                                                         </td>
                                                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                             <input
                                                                 type="number"
                                                                 min="0"
                                                                 value={item.current_stock}
                                                                 onChange={(e) => handleSkuInventoryChange(index, 'current_stock', parseInt(e.target.value))}
                                                                 className="p-2 focus:ring-primary-500 focus:border-primary-500 block w-24 shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                             />
                                                         </td>
                                                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                             <input
                                                                 type="number"
                                                                 min="0"
                                                                 value={item.threshold}
                                                                 onChange={(e) => handleSkuInventoryChange(index, 'threshold', parseInt(e.target.value))}
                                                                 className="p-2 focus:ring-primary-500 focus:border-primary-500 block w-24 shadow-sm sm:text-sm border-gray-300 rounded-md"
                                                             />
                                                         </td>
                                                         <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                                             <button
                                                                 type="button"
                                                                 onClick={() => handleRemoveProduct(index)}
                                                                 className="text-red-600 hover:text-red-900"
                                                             >
                                                                 <TrashIcon className="h-5 w-5" />
                                                             </button>
                                                         </td>
                                                     </tr>
                                                 ))}
                                                 </tbody>
                                             </table>
                                         </div>
                                     )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="px-6 py-4 border-t flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            {partType ? 'Update' : 'Create'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PartTypeForm;
